import { useCallback, useEffect, useState } from "react";
import { ConstellationAPI } from "../infrastructure/api/Constellation.api";

export type SessionSnapshot = { url: string; name: string };

/**
 * Loads a session's constellation snapshots (count + names + streamed images)
 * and manages the object-URL lifecycle. Application layer so UI never touches
 * the API/infra directly. Returns items in snapshot order; failed image loads
 * are dropped while keeping each surviving item's own name.
 */
export function useSessionSnapshots(sessionId: string | undefined): {
    items: SessionSnapshot[];
} {
    const [items, setItems] = useState<SessionSnapshot[]>([]);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        const created: string[] = [];
        (async () => {
            try {
                const { data } = await ConstellationAPI.getSessionSnapshotCount(sessionId);
                const count = data.count ?? 0;
                const names = data.names ?? [];
                if (count <= 0) return;
                const blobs = await Promise.all(
                    Array.from({ length: count }, (_, i) =>
                        ConstellationAPI.getSessionSnapshot(sessionId, i)
                            .then((res) => URL.createObjectURL(res.data))
                            .catch(() => null),
                    ),
                );
                if (cancelled) {
                    blobs.forEach((u) => u && URL.revokeObjectURL(u));
                    return;
                }
                const ok: SessionSnapshot[] = [];
                blobs.forEach((u, i) => {
                    if (!u) return;
                    created.push(u);
                    ok.push({ url: u, name: names[i] ?? "" });
                });
                setItems(ok);
            } catch {
                if (!cancelled) setItems([]);
            }
        })();
        return () => {
            cancelled = true;
            created.forEach((u) => URL.revokeObjectURL(u));
        };
    }, [sessionId]);

    return { items };
}

/**
 * Streams a thumbnail per snapshot index for a session, re-fetching whenever the
 * set of snapshots changes (add/delete). Returns object URLs aligned by index
 * (null while loading / on failure).
 */
export function useSnapshotThumbnails(
    sessionId: string | undefined,
    // A stable key per snapshot (its url) so the effect re-runs on add/delete.
    keys: string[],
): (string | null)[] {
    const [thumbs, setThumbs] = useState<(string | null)[]>([]);
    const signature = keys.join("|");

    useEffect(() => {
        if (!sessionId || keys.length === 0) {
            setThumbs([]);
            return;
        }
        let cancelled = false;
        const created: string[] = [];
        (async () => {
            const blobs = await Promise.all(
                keys.map((_, i) =>
                    ConstellationAPI.getSessionSnapshot(sessionId, i)
                        .then((res) => URL.createObjectURL(res.data))
                        .catch(() => null),
                ),
            );
            if (cancelled) {
                blobs.forEach((u) => u && URL.revokeObjectURL(u));
                return;
            }
            blobs.forEach((u) => u && created.push(u));
            setThumbs(blobs);
        })();
        return () => {
            cancelled = true;
            created.forEach((u) => URL.revokeObjectURL(u));
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, signature]);

    return thumbs;
}

/**
 * Returns a function that attaches a constellation board to an existing session
 * and resolves to the board's route path.
 */
export function useLaunchConstellationForSession(): (
    sessionId: string,
) => Promise<string> {
    return useCallback(async (sessionId: string) => {
        const { data } = await ConstellationAPI.launchForSession(sessionId);
        return `/constellation/${data.board.id}`;
    }, []);
}

/**
 * Returns a function that attaches a couple constellation board to an existing
 * session. The server derives session_type from the session record automatically.
 */
export function useLaunchConstellationCoupleForSession(): (
    sessionId: string,
) => Promise<string> {
    return useCallback(async (sessionId: string) => {
        const { data } = await ConstellationAPI.launchForSession(sessionId);
        return `/constellation/${data.board.id}`;
    }, []);
}
